/**
 * AI NEWS 价格走势图（纯 SVG，零依赖）
 *
 * 渲染文章内 ```chart 代码块的 JSON 数据：
 *   {"title":"H100 按时租赁价格走势","unit":"$/hr","series":[{"name":"Nebius","points":[{"date":"2026-09-10","value":2.49}]}]}
 *
 * 特性：多系列折线、图例、网格、轴标签、末值标注、原生 <title> 悬停提示。
 * 解析失败返回 null（调用方降级为普通代码块展示）。
 */

export interface ChartPoint {
  date: string;
  value: number;
}

export interface ChartSeries {
  name: string;
  points: ChartPoint[];
}

export interface ChartData {
  title?: string;
  unit?: string;
  series: ChartSeries[];
}

/** 解析 chart 代码块文本，非法返回 null */
export function parseChartJson(text: string): ChartData | null {
  try {
    const raw = JSON.parse(text.trim());
    const series: ChartSeries[] = Array.isArray(raw?.series)
      ? raw.series
          .map((s: any) => ({
            name: String(s?.name || '').slice(0, 30),
            points: (Array.isArray(s?.points) ? s.points : [])
              .map((p: any) => ({ date: String(p?.date || ''), value: Number(p?.value) }))
              .filter((p: ChartPoint) => p.date && Number.isFinite(p.value)),
          }))
          .filter((s: ChartSeries) => s.name && s.points.length > 0)
      : [];
    if (series.length === 0) return null;
    return {
      title: raw?.title ? String(raw.title).slice(0, 60) : undefined,
      unit: raw?.unit ? String(raw.unit).slice(0, 10) : undefined,
      series,
    };
  } catch {
    return null;
  }
}

// 亮色主题系列色板（靛蓝 → 青 → 玫红 → 琥珀 → 翠绿 → 紫）
const COLORS = ['#4f46e5', '#0891b2', '#db2777', '#d97706', '#059669', '#7c3aed'];

const W = 800;
const H = 380;
const PAD = { top: 42, right: 64, bottom: 46, left: 62 };

export default function PriceChart({ data }: { data: ChartData }) {
  // x 轴刻度 = 全部日期并集（升序），每个点按 date 定位 —— 系列长度不同也能对齐
  const dates = Array.from(new Set(data.series.flatMap((s) => s.points.map((p) => p.date)))).sort();
  const xOf = (date: string) => {
    const i = dates.indexOf(date);
    if (dates.length <= 1) return PAD.left;
    return PAD.left + (i / (dates.length - 1)) * (W - PAD.left - PAD.right);
  };

  const allValues = data.series.flatMap((s) => s.points.map((p) => p.value));
  let min = Math.min(...allValues);
  let max = Math.max(...allValues);
  if (min === max) {
    min -= Math.abs(min) * 0.1 || 1;
    max += Math.abs(max) * 0.1 || 1;
  } else {
    const span = max - min;
    min -= span * 0.08;
    max += span * 0.08;
  }
  const yOf = (v: number) =>
    H - PAD.bottom - ((v - min) / (max - min)) * (H - PAD.top - PAD.bottom);

  // y 轴 4 条网格线
  const gridLines = [0, 1 / 3, 2 / 3, 1].map((t) => {
    const v = min + (max - min) * (1 - t);
    return { y: PAD.top + t * (H - PAD.top - PAD.bottom), label: formatNum(v) };
  });

  // x 轴日期标签抽稀（最多 8 个）
  const labelStep = Math.max(1, Math.ceil(dates.length / 8));
  const xLabels = dates.filter((_, i) => i % labelStep === 0 || i === dates.length - 1);

  return (
    <div className="my-6 rounded-xl border border-slate-100 bg-white px-3 py-4 sm:px-5">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
        {/* 标题 + 单位 */}
        {data.title && (
          <text x={PAD.left - 46} y={20} fill="#1e293b" fontSize="14" fontWeight="600">
            {data.title}
            {data.unit && <tspan fill="#94a3b8" fontSize="12" fontWeight="400">（{data.unit}）</tspan>}
          </text>
        )}

        {/* 网格 + y 标签 */}
        {gridLines.map((g, i) => (
          <g key={i}>
            <line x1={PAD.left} y1={g.y} x2={W - PAD.right} y2={g.y} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="3 4" />
            <text x={PAD.left - 8} y={g.y + 4} fill="#94a3b8" fontSize="11" textAnchor="end" className="font-mono">
              {g.label}
            </text>
          </g>
        ))}

        {/* x 轴日期标签 */}
        {xLabels.map((d) => (
          <text key={d} x={xOf(d)} y={H - PAD.bottom + 20} fill="#94a3b8" fontSize="11" textAnchor="middle" className="font-mono">
            {d.slice(5)}
          </text>
        ))}

        {/* 系列 */}
        {data.series.map((s, si) => {
          const color = COLORS[si % COLORS.length];
          const pts = [...s.points].sort((a, b) => dates.indexOf(a.date) - dates.indexOf(b.date));
          const path = pts.map((p) => `${xOf(p.date)},${yOf(p.value)}`).join(' ');
          const last = pts[pts.length - 1];
          return (
            <g key={s.name}>
              <polyline points={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              {pts.map((p) => (
                <circle key={p.date} cx={xOf(p.date)} cy={yOf(p.value)} r="3.2" fill="#ffffff" stroke={color} strokeWidth="2">
                  <title>{`${s.name} · ${p.date}: ${formatNum(p.value)}${data.unit || ''}`}</title>
                </circle>
              ))}
              {/* 末值标注 */}
              <text
                x={xOf(last.date) + 8}
                y={yOf(last.value) + 4}
                fill={color}
                fontSize="11"
                fontWeight="600"
                className="font-mono"
              >
                {formatNum(last.value)}
              </text>
            </g>
          );
        })}
      </svg>

      {/* 图例 */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 px-2">
        {data.series.map((s, si) => (
          <span key={s.name} className="inline-flex items-center gap-1.5 text-xs text-slate-400">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: COLORS[si % COLORS.length] }} />
            <span className="font-mono">{s.name}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function formatNum(v: number): string {
  if (Math.abs(v) >= 1000) return v.toFixed(0);
  if (Math.abs(v) >= 100) return v.toFixed(1);
  return String(Math.round(v * 100) / 100);
}
